import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiGet, apiPostForm, APIError } from "@/api/client";
import {
  accountAutoMapHuntUsagePath,
  accountModuleEditPath,
  accountModuleResolvePath,
  accountModulesPath,
} from "@/api/endpoints";
import type {
  AccountModuleEditResponse,
  AccountModuleResolveResponse,
  AccountModulesResponse,
  MapUsageEntry,
  MapUsageResponse,
  MapUsageReference,
} from "@/api/types";
import { getGameBundle, type GameBundleResponse } from "@/api/games";
import { QueryView } from "@/components/ui/QueryView";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Field, Textarea } from "@/components/ui/Input";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Checkbox } from "@/components/ui/Checkbox";
import { ModuleJobStatus } from "@/components/jobs/ModuleJobStatus";
import { toast } from "@/components/ui/Sonner";
import { AutoAuctionBidConfigForm } from "@/components/modules/ModuleConfigForms";
import {
  formatModuleConfigDrift,
  formatModuleMatrixColor,
  formatModuleRuntimeStatus,
} from "@/lib/module-status";

export const Route = createFileRoute("/accounts/$accountId/modules/$module/edit")({
  component: AccountModuleEditRoute,
});

function AccountModuleEditRoute() {
  const { accountId, module } = Route.useParams();
  return <AccountModuleEditView accountId={accountId} moduleName={module} />;
}

export function AccountModuleEditView({
  accountId,
  moduleName,
}: {
  accountId: string;
  moduleName: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [jobID, setJobID] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["account-modules", accountId],
    queryFn: () => apiGet<AccountModulesResponse>(accountModulesPath(accountId)),
  });

  const mut = useMutation({
    mutationFn: ({
      desired,
      action,
    }: {
      desired: string;
      action: "save" | "save_and_apply";
    }) =>
      apiPostForm<AccountModuleEditResponse>(
        accountModuleEditPath(accountId, moduleName),
        {
          desired_config_json: desired,
          action,
        },
      ),
    onSuccess: (data) => {
      toast.success(data.job_id ? "已保存并下发" : "已保存");
      setJobID(data.job_id ?? null);
      void queryClient.invalidateQueries({ queryKey: ["account-modules", accountId] });
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
    },
    onError: (e) => {
      const message = e instanceof APIError ? e.message : (e as Error).message;
      toast.error("保存失败", { description: message });
    },
  });

  const resolveMut = useMutation({
    mutationFn: (decision: string) =>
      apiPostForm<AccountModuleResolveResponse>(
        accountModuleResolvePath(accountId, moduleName),
        { decision },
      ),
    onSuccess: (data) => {
      toast.success(`已处理漂移：${data.decision}`);
      void queryClient.invalidateQueries({ queryKey: ["account-modules", accountId] });
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
    },
    onError: (e) => {
      const message = e instanceof APIError ? e.message : (e as Error).message;
      toast.error("处理漂移失败", { description: message });
    },
  });

  return (
    <div className="space-y-4">
      <button
        type="button"
        className="text-xs underline"
        onClick={() => navigate({ to: "/accounts/$accountId", params: { accountId } })}
      >
        ← 返回账号详情
      </button>

      <QueryView query={q}>
        {(data) => {
          const config = data.configs.find((c) => c.module === moduleName);
          const row = data.rows.find((r) => r.module === moduleName);
          const desired = config?.desired_config_json || "{}";
          const current = config?.current_config_json || "{}";
          return (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>状态</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-[140px_1fr] gap-y-1 text-xs">
                    <dt className="text-[hsl(var(--muted-foreground))]">账号</dt>
                    <dd>{data.account.game_account}</dd>
                    <dt className="text-[hsl(var(--muted-foreground))]">状态</dt>
                    <dd>{formatModuleMatrixColor(row?.color_state)}</dd>
                    <dt className="text-[hsl(var(--muted-foreground))]">运行状态</dt>
                    <dd>{formatModuleRuntimeStatus(row?.runtime_status) || "—"}</dd>
                    <dt className="text-[hsl(var(--muted-foreground))]">配置一致性</dt>
                    <dd>
                      <StatusBadge
                        variant={
                          (row?.config_drift ?? config?.config_drift) === "clean"
                            ? "ok"
                            : (row?.config_drift ?? config?.config_drift) === "ignored"
                              ? "neutral"
                              : "warn"
                        }
                      >
                        {formatModuleConfigDrift(
                          row?.config_drift ?? config?.config_drift,
                        )}
                      </StatusBadge>
                    </dd>
                  </dl>
                  {config ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={resolveMut.isPending}
                        onClick={() => resolveMut.mutate("adopt_current")}
                      >
                        采纳 current
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={resolveMut.isPending}
                        onClick={() => resolveMut.mutate("keep_desired")}
                      >
                        保留 desired
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={resolveMut.isPending}
                        onClick={() => resolveMut.mutate("ignore")}
                      >
                        忽略漂移
                      </Button>
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              {moduleName === "auto_map_hunt" ? (
                <AutoMapHuntEditor
                  accountGameID={data.account.game_id || "fgcq"}
                  accountId={accountId}
                  desired={desired}
                  current={current}
                  submitting={mut.isPending}
                  onSubmit={(nextDesired, apply) =>
                    mut.mutate({
                      desired: nextDesired,
                      action: apply ? "save_and_apply" : "save",
                    })
                  }
                />
              ) : moduleName === "auto_auction_bid" ? (
                <AutoAuctionBidAccountEditor
                  desired={desired}
                  current={current}
                  submitting={mut.isPending}
                  onSubmit={(nextDesired, apply) =>
                    mut.mutate({
                      desired: nextDesired,
                      action: apply ? "save_and_apply" : "save",
                    })
                  }
                />
              ) : (
                <GenericModuleEditor
                  desired={desired}
                  current={current}
                  submitting={mut.isPending}
                  onSubmit={(nextDesired, apply) =>
                    mut.mutate({
                      desired: nextDesired,
                      action: apply ? "save_and_apply" : "save",
                    })
                  }
                />
              )}

              {jobID ? <ModuleJobStatus jobID={jobID} /> : null}
            </div>
          );
        }}
      </QueryView>
    </div>
  );
}

function AutoAuctionBidAccountEditor({
  desired,
  current,
  submitting,
  onSubmit,
}: {
  desired: string;
  current: string;
  submitting: boolean;
  onSubmit: (desired: string, apply: boolean) => void;
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_420px]">
      <Card>
        <CardHeader>
          <CardTitle>拍卖竞价账号覆盖</CardTitle>
        </CardHeader>
        <CardContent>
          <AutoAuctionBidConfigForm
            rawConfig={desired}
            scope="instance"
            disabled={submitting}
            footer={
              <div className="flex justify-end gap-2">
                <Button
                  type="submit"
                  size="sm"
                  variant="outline"
                  disabled={submitting}
                  data-action="save"
                >
                  保存
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={submitting}
                  data-action="save_and_apply"
                >
                  保存并下发
                </Button>
              </div>
            }
            onSubmit={(nextDesired, action) =>
              onSubmit(nextDesired, action === "save_and_apply")
            }
          />
        </CardContent>
      </Card>
      <ReadonlyJSONCard title="current_config_json" value={current} />
    </div>
  );
}

function GenericModuleEditor({
  desired,
  current,
  submitting,
  onSubmit,
}: {
  desired: string;
  current: string;
  submitting: boolean;
  onSubmit: (desired: string, apply: boolean) => void;
}) {
  const [text, setText] = useState(() => prettyJSON(desired));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setText(prettyJSON(desired));
    setError(null);
  }, [desired]);

  function update(next: string) {
    setText(next);
    try {
      JSON.parse(next || "{}");
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  function submit(apply: boolean) {
    if (error) return;
    onSubmit(text, apply);
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>账号覆盖 JSON</CardTitle>
        </CardHeader>
        <CardContent>
          <Field error={error ?? undefined}>
            <Textarea
              rows={22}
              value={text}
              onChange={(e) => update(e.target.value)}
              spellCheck={false}
              className="font-mono text-xs"
            />
          </Field>
          <div className="mt-3 flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!!error || submitting}
              onClick={() => submit(false)}
            >
              保存
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!!error || submitting}
              onClick={() => submit(true)}
            >
              保存并下发
            </Button>
          </div>
        </CardContent>
      </Card>
      <ReadonlyJSONCard title="current_config_json" value={current} />
    </div>
  );
}

function AutoMapHuntEditor({
  accountGameID,
  accountId,
  desired,
  current,
  submitting,
  onSubmit,
}: {
  accountGameID: string;
  accountId: string;
  desired: string;
  current: string;
  submitting: boolean;
  onSubmit: (desired: string, apply: boolean) => void;
}) {
  const bundleQ = useQuery({
    queryKey: ["game-bundle", accountGameID],
    queryFn: () => getGameBundle(accountGameID),
  });
  const usageQ = useQuery({
    queryKey: ["account-auto-map-hunt-usage", accountId],
    queryFn: () => apiGet<MapUsageResponse>(accountAutoMapHuntUsagePath(accountId)),
    staleTime: 15_000,
  });
  const parsed = useMemo(() => parseAutoMapHuntDesired(desired), [desired]);
  const [enabled, setEnabled] = useState(parsed.enabled);
  const [cutUpgradeEnabled, setCutUpgradeEnabled] = useState(parsed.cutUpgradeEnabled);
  const [selectAllMaps, setSelectAllMaps] = useState(parsed.selectAllMaps);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(parsed.selectedMapIds),
  );

  useEffect(() => {
    setEnabled(parsed.enabled);
    setCutUpgradeEnabled(parsed.cutUpgradeEnabled);
    setSelectAllMaps(parsed.selectAllMaps);
    setSelected(new Set(parsed.selectedMapIds));
  }, [parsed]);

  function buildDesired(bundle: GameBundleResponse) {
    const ordered = bundle.bundle.maps
      .map((m) => m.map_id)
      .filter((id) => selected.has(id));
    return JSON.stringify(
      selectAllMaps
        ? {
            enabled,
            cutUpgrade: {
              enabled: cutUpgradeEnabled,
              mode: "privilege_map",
              stopAtMultiplier: 8,
            },
            selectAllMaps: true,
          }
        : {
            enabled,
            cutUpgrade: {
              enabled: cutUpgradeEnabled,
              mode: "privilege_map",
              stopAtMultiplier: 8,
            },
            selectAllMaps: false,
            selectedMapIds: ordered,
          },
      null,
      2,
    );
  }

  return (
    <QueryView query={bundleQ}>
      {(bundle) => (
        <div className="grid gap-4 lg:grid-cols-[1fr_420px]">
          <Card>
            <CardHeader>
              <CardTitle>auto_map_hunt 账号覆盖</CardTitle>
            </CardHeader>
            <CardContent>
              <label className="mb-3 flex items-center gap-2 text-sm">
                <Checkbox
                  checked={enabled}
                  onCheckedChange={(v) => setEnabled(v === true)}
                />
                启用自动挂机
              </label>
              <label className="mb-3 flex items-center gap-2 text-sm">
                <Checkbox
                  checked={cutUpgradeEnabled}
                  disabled={submitting}
                  onCheckedChange={(v) => setCutUpgradeEnabled(v === true)}
                />
                升级切割
              </label>
              <label className="mb-3 flex items-center gap-2 text-sm">
                <Checkbox
                  checked={selectAllMaps}
                  disabled={submitting}
                  onCheckedChange={(v) => setSelectAllMaps(v === true)}
                />
                动态全选地图
              </label>
              <div className="mb-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={submitting || selectAllMaps}
                  onClick={() =>
                    setSelected(new Set(selectableAutoMapIDs(bundle)))
                  }
                >
                  全选可选地图
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={submitting || selectAllMaps}
                  onClick={() =>
                    setSelected((prev) => {
                      const selectable = selectableAutoMapIDs(bundle);
                      return new Set(selectable.filter((id) => !prev.has(id)));
                    })
                  }
                >
                  反选可选地图
                </Button>
              </div>
              <div className="space-y-4">
                {bundle.bundle.continents.map((continent) => {
                  const maps = bundle.bundle.maps.filter(
                    (m) => m.continent_id === continent.continent_id,
                  );
                  if (maps.length === 0) return null;
                  return (
                    <section key={continent.continent_id} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium">{continent.name}</h3>
                        {!continent.enabled ? (
                          <StatusBadge variant="neutral">停用</StatusBadge>
                        ) : null}
                      </div>
                      <div className="grid gap-2 md:grid-cols-2">
                        {maps.map((m) => {
                          const blocked =
                            !continent.enabled ||
                            m.unreachable;
                          const checked = selected.has(m.map_id);
                          const disabled = selectAllMaps || (blocked && !checked);
                          const usage = usageQ.data?.usage;
                          return (
                            <label
                              key={m.map_id}
                              className="flex items-start gap-2 rounded border border-[hsl(var(--border))] p-2 text-xs"
                            >
                              <Checkbox
                                checked={checked}
                                disabled={disabled}
                                onCheckedChange={(v) => {
                                  setSelected((prev) => {
                                    const next = new Set(prev);
                                    if (v === true) next.add(m.map_id);
                                    else next.delete(m.map_id);
                                    return next;
                                  });
                                }}
                              />
                              <span>
                                <span className="block font-mono">{m.map_id}</span>
                                <span>{m.name}</span>
                                {disabled ? (
                                  <span className="block text-[hsl(var(--muted-foreground))]">
                                    {m.unreachable_reason || "continent_disabled"}
                                  </span>
                                ) : null}
                                {usage ? <MapUsageHints usage={usage} mapID={m.map_id} /> : null}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={submitting}
                  onClick={() => onSubmit(buildDesired(bundle), false)}
                >
                  保存
                </Button>
                <Button
                  size="sm"
                  disabled={submitting}
                  onClick={() => onSubmit(buildDesired(bundle), true)}
                >
                  保存并下发
                </Button>
              </div>
            </CardContent>
          </Card>
          <div className="space-y-4">
            <MapUsageSummary query={usageQ} />
            <ReadonlyJSONCard title="账号覆盖预览" value={buildDesired(bundle)} />
            <ReadonlyJSONCard title="current_config_json" value={current} />
          </div>
        </div>
      )}
    </QueryView>
  );
}

function MapUsageSummary({
  query,
}: {
  query: UseQueryResult<MapUsageResponse, Error>;
}) {
  if (query.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>同区占用</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">加载中</p>
        </CardContent>
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>同区占用</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-[hsl(var(--danger))]">
            {(query.error as Error).message}
          </p>
        </CardContent>
      </Card>
    );
  }
  const usage = query.data?.usage;
  if (!usage) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>同区占用</CardTitle>
      </CardHeader>
      <CardContent>
        {!usage.has_server ? (
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            账号尚未上报区服，暂不能计算同区占用。
          </p>
        ) : (
          <dl className="grid grid-cols-[96px_1fr] gap-y-1 text-xs">
            <dt className="text-[hsl(var(--muted-foreground))]">当前区服</dt>
            <dd>{usage.server_id}</dd>
            <dt className="text-[hsl(var(--muted-foreground))]">归一区服</dt>
            <dd>{usage.canonical_server_id || usage.server_id}</dd>
            <dt className="text-[hsl(var(--muted-foreground))]">已配置</dt>
            <dd>{usage.configured.length}</dd>
            <dt className="text-[hsl(var(--muted-foreground))]">实时运行</dt>
            <dd>{freshRunningRefs(usage.running).length}</dd>
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function MapUsageHints({ usage, mapID }: { usage: MapUsageEntry; mapID: string }) {
  if (!usage.has_server) return null;
  const configured = usage.configured.filter((ref) => ref.map_id === mapID);
  const running = usage.running.filter((ref) => ref.map_id === mapID);
  const fresh = freshRunningRefs(running);
  const stale = running.filter((ref) => !isFreshRunning(ref));
  if (configured.length === 0 && running.length === 0) return null;
  return (
    <span className="mt-1 block space-y-0.5">
      {configured.length > 0 ? (
        <span className="block text-[hsl(var(--warn))]">
          同区 {configured.length} 个角色已配：{formatUsageNames(configured)}
        </span>
      ) : null}
      {fresh.length > 0 ? (
        <span className="block text-[hsl(var(--danger))]">
          实时占用：{formatUsageNames(fresh)}
        </span>
      ) : null}
      {stale.length > 0 ? (
        <span className="block text-[hsl(var(--muted-foreground))]">
          历史运行：{formatUsageNames(stale)}
        </span>
      ) : null}
    </span>
  );
}

function freshRunningRefs(refs: MapUsageReference[]) {
  return refs.filter(isFreshRunning);
}

function isFreshRunning(ref: MapUsageReference) {
  const lastSeenAt = Number(ref.last_seen_at || 0);
  if (lastSeenAt <= 0) return false;
  return Date.now() - lastSeenAt <= 90_000;
}

function formatUsageNames(refs: MapUsageReference[]) {
  return refs
    .map((ref) => ref.role_name || ref.game_account || `#${ref.account_id}`)
    .slice(0, 4)
    .join("、") + (refs.length > 4 ? ` 等 ${refs.length} 个` : "");
}

function ReadonlyJSONCard({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Textarea
          rows={22}
          value={prettyJSON(value)}
          readOnly
          spellCheck={false}
          className="font-mono text-xs"
        />
      </CardContent>
    </Card>
  );
}

function prettyJSON(raw: string) {
  try {
    return JSON.stringify(JSON.parse(raw || "{}"), null, 2);
  } catch {
    return raw;
  }
}

function parseAutoMapHuntDesired(raw: string): {
  enabled: boolean;
  cutUpgradeEnabled: boolean;
  selectAllMaps: boolean;
  selectedMapIds: string[];
} {
  try {
    const parsed = JSON.parse(raw || "{}") as {
      enabled?: boolean;
      cutUpgrade?: { enabled?: boolean };
      selectAllMaps?: boolean;
      selectedMapIds?: unknown[];
    };
    return {
      enabled: parsed.enabled === true,
      cutUpgradeEnabled:
        parsed.cutUpgrade?.enabled === undefined ? true : parsed.cutUpgrade.enabled === true,
      selectAllMaps: parsed.selectAllMaps === true,
      selectedMapIds: Array.isArray(parsed.selectedMapIds)
        ? parsed.selectedMapIds
            .map((v) => String(v).trim())
            .filter((v) => v.length > 0)
        : [],
    };
  } catch {
    return { enabled: false, cutUpgradeEnabled: true, selectAllMaps: false, selectedMapIds: [] };
  }
}

function selectableAutoMapIDs(bundle: GameBundleResponse) {
  const enabledContinents = new Map(
    bundle.bundle.continents.map((c) => [c.continent_id, c.enabled]),
  );
  return bundle.bundle.maps
    .filter(
      (m) =>
        enabledContinents.get(m.continent_id) !== false &&
        !m.unreachable,
    )
    .map((m) => m.map_id);
}
